import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CheckmarkLine,
  ChevronDownLine,
  ChevronLeftLine,
  ChevronRightLine,
  ZoomInLine,
  ZoomOutLine,
} from "@dropbox/dig-icons/assets";
import type { FileDownloadStatus } from "@mirage/analytics/events/enums/file_download_status";
import { tagged } from "@mirage/service-logging";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ZOOM_LEVELS } from "./constants";
import styles from "./DocumentPreviewArea.module.css";

import { useComponentConfig } from "@mirage/component-config";
import type { SearchResult } from "@mirage/service-dbx-api";

const logger = tagged("enhanced-file-viewer");

// dynamic import functions to avoid issues
const getPdfLibrary = async () => {
  const { getPdfLibrary: actualGetPdfLibrary } = await import(
    "@mirage/shared/utils/pdf-worker-loader"
  );
  return actualGetPdfLibrary();
};

const getPdfWorkerUrl = async (): Promise<string> => {
  const { getPdfWorkerUrl: actualGetPdfWorkerUrl } = await import(
    "@mirage/shared/utils/pdf-worker-loader"
  );
  return actualGetPdfWorkerUrl();
};

interface PDFDocument {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PDFPage>;
}

interface PDFPage {
  getViewport: (options: { scale: number }) => PDFViewport;
  render: (context: PDFRenderContext) => PDFRenderTask;
}

interface PDFViewport {
  width: number;
  height: number;
  scale: number;
}

interface PDFRenderContext {
  canvasContext: CanvasRenderingContext2D;
  viewport: PDFViewport;
}

interface PDFRenderTask {
  promise: Promise<void>;
}

interface PDFLibrary {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  version: string;
  getDocument: (options: PDFDocumentOptions) => PDFLoadingTask;
}

interface PDFDocumentOptions {
  url: string;
  httpHeaders?: Record<string, string>;
  withCredentials?: boolean;
  timeoutMs?: number;
}

interface PDFLoadingTask {
  promise: Promise<PDFDocument>;
  onProgress?: (progress: { loaded: number; total: number }) => void;
  destroy: () => void;
}

declare global {
  interface Window {
    handleViewerStrategyFailure?: () => void;
  }
}

type FileType = "pdf" | "image" | "video" | "audio" | "text" | "unknown";
type ViewerType =
  | "direct_pdfjs"
  | "iframe"
  | "image"
  | "video"
  | "text"
  | "unsupported";

interface ViewerProps {
  result: SearchResult;
  zoomLevel: number;
  currentPage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomChange?: (level: number) => void;
  onPageChange: (page: number) => void;
  onViewerUpdate?: (updates: { totalPages?: number }) => void;
  // add optional callback for viewer to report strategy failure back to parent
  onStrategyFailure?: () => void;
  onFileDownloadComplete?: (status: FileDownloadStatus) => void;
}

interface ViewerStrategy {
  type: ViewerType;
  canHandle: (fileUrl: string, mimeType?: string) => boolean;
  priority: number;
  component: React.ComponentType<ViewerProps>;
}

const DirectPdfViewer: React.FC<ViewerProps> = ({
  result,
  zoomLevel,
  currentPage,
  onZoomIn,
  onZoomOut,
  onZoomChange,
  onPageChange,
  onViewerUpdate,
  onStrategyFailure,
  onFileDownloadComplete,
}) => {
  const { t } = useTranslation();

  const pdfRef = useRef<PDFDocument | null>(null);
  const pdfjsRef = useRef<PDFLibrary | null>(null);

  const [pdfjsLibrary, setPdfjsLibrary] = useState<PDFLibrary | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(currentPage);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const pdfUrl = result.url || result.previewUrl;

  logger.debug("DirectPdfViewer rendered", {
    pdfUrl,
    zoomLevel,
    currentPage,
    isLoading,
    error,
  });

  useEffect(() => {
    // TODO(dash-backend): Replace direct pdf.js initialization with metadata fetch
    // from `/2/dash/get_document_metadata` so we can retrieve a presigned content URL
    // via `/2/dash/get_document_content` (or stream) instead of loading the raw
    // external URL which may hit CORS issues. The presigned URL can then be passed
    // to PDF.js below.
    const initPdfLib = async () => {
      try {
        logger.debug("Initializing PDF.js library");

        const pdfjsLib = await getPdfLibrary();
        if (!pdfjsLib) {
          throw new Error("Failed to load PDF.js library");
        }

        const workerUrl = await getPdfWorkerUrl();
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

        if (!pdfjsLib.getDocument) {
          throw new Error("PDF.js library is missing getDocument method");
        }

        pdfjsRef.current = pdfjsLib;
        setPdfjsLibrary(pdfjsLib);
        logger.debug("PDF.js library initialized successfully", {
          version: pdfjsLib.version,
          workerUrl: workerUrl,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        logger.warn("PDF.js initialization failed", {
          error: errorMessage,
        });

        // fallback via callback if provided
        if (onStrategyFailure) {
          setTimeout(() => onStrategyFailure(), 100);
        } else {
          setError(t("no_preview_available"));
          setIsLoading(false);
        }
      }
    };

    initPdfLib();
  }, [t, onStrategyFailure]);

  useEffect(() => {
    if (!pdfjsLibrary || !pdfUrl) {
      if (!pdfUrl) {
        setError("No PDF URL provided");
        setIsLoading(false);
      }
      return;
    }

    const pdfjs = pdfjsLibrary;
    setIsLoading(true);
    setError(null);

    logger.debug("Attempting to load PDF", { url: pdfUrl });

    const timeoutDuration = 30000;
    const timeoutId = setTimeout(() => {
      logger.warn("PDF loading timeout, triggering fallback");
      onFileDownloadComplete?.("timeout");
      if (onStrategyFailure) {
        onStrategyFailure();
      }
    }, timeoutDuration);

    // TODO(dash-backend): once `/2/dash/get_document_content` is available we
    // should call it here to obtain a proxy or presigned URL for `pdfUrl`.
    // That will eliminate many CORS failures and allow authenticated access.
    const loadingTask = pdfjs.getDocument({
      url: pdfUrl,
      httpHeaders: {},
      withCredentials: false,
      timeoutMs: timeoutDuration,
    });

    loadingTask.promise
      .then((pdfDoc: PDFDocument) => {
        logger.debug("PDF loaded successfully", {
          pages: pdfDoc.numPages,
          url: pdfUrl,
        });
        clearTimeout(timeoutId);
        pdfRef.current = pdfDoc;
        setNumPages(pdfDoc.numPages);
        setIsLoading(false);
        setError(null);
        onFileDownloadComplete?.("success");

        if (onViewerUpdate) {
          onViewerUpdate({ totalPages: pdfDoc.numPages });
        }
      })
      .catch((err: Error) => {
        logger.warn("PDF load failed", { error: err.message, url: pdfUrl });
        clearTimeout(timeoutId);
        onFileDownloadComplete?.("error");

        const isCorsError =
          err.message.includes("cors") ||
          err.message.includes("CORS") ||
          err.message.includes("cross-origin") ||
          err.message.toLowerCase().includes("network error") ||
          err.name === "NetworkError" ||
          err.message.includes("Failed to fetch") ||
          err.message.includes("ERR_BLOCKED_BY_CLIENT");

        if (isCorsError) {
          // TODO(dash-backend): remove once we consistently use backend proxy for PDFs.
          logger.warn("PDF.js CORS error - PDF cannot be loaded directly", {
            error: err.message,
            url: pdfUrl,
            suggestedAction: "Use iframe fallback or proxy",
          });

          // TODO(dash-frontend): once backend proxy is used this manual fallback
          // can be revisited/removed.
          // Trigger fallback via host page if available
          const fallbackDelay = 100;
          if (onStrategyFailure) {
            logger.debug("Triggering strategy fallback due to CORS error");
            setTimeout(() => onStrategyFailure(), fallbackDelay);
          } else {
            setError(t("no_preview_available"));
            setIsLoading(false);
          }
          return;
        }

        logger.warn("Direct PDF.js load failed", {
          error: err.message,
          url: pdfUrl,
        });

        const isCriticalError =
          err.message.includes("Invalid PDF") ||
          err.message.includes("PDF header not found") ||
          err.message.includes("Missing PDF header");

        if (isCriticalError) {
          logger.warn("Critical PDF error, triggering fallback");
          const fallbackDelay = 100;
          if (onStrategyFailure) {
            setTimeout(() => onStrategyFailure(), fallbackDelay);
          } else {
            setError(t("no_preview_available"));
            setIsLoading(false);
          }
        } else {
          setError(t("no_preview_available"));
          setIsLoading(false);
        }
      });

    return () => {
      clearTimeout(timeoutId);
      if (loadingTask && typeof loadingTask.destroy === "function") {
        loadingTask.destroy();
      }
    };
  }, [
    pdfjsLibrary,
    pdfUrl,
    t,
    onViewerUpdate,
    onStrategyFailure,
    onFileDownloadComplete,
  ]);

  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || !canvasRef.current || !containerRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        const canvas = canvasRef.current!;
        const context = canvas.getContext("2d")!;

        const containerWidth = containerRef.current!.offsetWidth - 40;
        const defaultScale = 1.0;
        const viewport = page.getViewport({ scale: defaultScale });
        const calculatedScale = Math.min(containerWidth / viewport.width, 1.5);
        const finalScale = calculatedScale * (zoomLevel / 100);

        const scaledViewport = page.getViewport({ scale: finalScale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };

        await page.render(renderContext).promise;
        logger.debug("Page rendered successfully", { page: pageNumber });
      } catch (err) {
        logger.warn("PDF page render failed", {
          error: err instanceof Error ? err.message : String(err),
          page: pageNumber,
          url: pdfUrl,
        });
      }
    };

    renderPage();
  }, [pdfRef, pageNumber, zoomLevel, pdfUrl]);

  const goToPrevPage = useCallback(() => {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1;
      setPageNumber(newPage);
      onPageChange(newPage);
    }
  }, [pageNumber, onPageChange]);

  const goToNextPage = useCallback(() => {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1;
      setPageNumber(newPage);
      onPageChange(newPage);
    }
  }, [pageNumber, numPages, onPageChange]);

  useEffect(() => {
    setPageNumber(currentPage);
  }, [currentPage]);

  if (error) {
    return (
      <div className={styles.previewArea}>
        <div className={styles.placeholderContent}>
          <Text size="large" color="subtle">
            {error}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.previewArea}>
      <div className={styles.previewControls}>
        <div className={styles.paginationControls}>
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ChevronLeftLine} />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            aria-label={t("previous_page")}
          />
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ChevronRightLine} />}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            aria-label={t("next_page")}
          />
        </div>

        <div className={(styles as Record<string, string>).zoomGroup}>
          <Text size="small" className={styles.zoomInfo}>
            {t("page_of_total", {
              currentPage: pageNumber,
              totalPages: numPages,
            })}
          </Text>

          <Menu.Wrapper
            onSelection={(value) => {
              const level = Number(value);
              if (!Number.isNaN(level) && onZoomChange) {
                onZoomChange(level);
              }
            }}
          >
            {({ getTriggerProps, getContentProps }) => (
              <>
                <Button
                  {...getTriggerProps()}
                  variant="outline"
                  size="medium"
                  withIconEnd={<UIIcon src={ChevronDownLine} />}
                  className={styles.zoomPercentage}
                >
                  {t("zoom_percentage", { value: zoomLevel })}
                </Button>
                <Menu.Content {...getContentProps()} placement="top">
                  {ZOOM_LEVELS.map((level) => (
                    <Menu.ActionItem
                      key={level}
                      value={level.toString()}
                      withRightAccessory={
                        level === zoomLevel ? (
                          <UIIcon src={CheckmarkLine} size="small" />
                        ) : undefined
                      }
                    >
                      {t("zoom_percentage", { value: level })}
                    </Menu.ActionItem>
                  ))}
                </Menu.Content>
              </>
            )}
          </Menu.Wrapper>
        </div>

        <div className={styles.zoomButtons}>
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomInLine} />}
            onClick={onZoomIn}
            disabled={zoomLevel >= 200}
            aria-label={t("zoom_in")}
          />
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomOutLine} />}
            onClick={onZoomOut}
            disabled={zoomLevel <= 50}
            aria-label={t("zoom_out")}
          />
        </div>
      </div>

      <div className={styles.documentPreview}>
        <div className={styles.documentPage}>
          {isLoading && (
            <div className={styles.placeholderContent}>
              <Spinner monochromatic size="small" />
            </div>
          )}
          <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
            <canvas
              ref={canvasRef}
              className={styles.pdfCanvas}
              style={{
                display: isLoading ? "none" : "block",
                maxWidth: "100%",
                height: "auto",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// TODO(dash-backend): IframeViewer should eventually point to a rendered preview
// served by `/2/dash/get_document_preview` (or similar) instead of the raw file URL.
const IframeViewer: React.FC<ViewerProps> = ({
  result,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomChange: _onZoomChange,
  onViewerUpdate,
  onStrategyFailure,
  onFileDownloadComplete,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileUrl = result.url || result.previewUrl;

  const isValidUrl = useCallback((url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    } catch {
      return false;
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    logger.debug("Iframe viewer loaded successfully", { url: fileUrl });
    setIsLoading(false);
    setError(null);
    onFileDownloadComplete?.("success");

    if (onViewerUpdate) {
      onViewerUpdate({ totalPages: 1 });
    }
  }, [fileUrl, onViewerUpdate, onFileDownloadComplete]);

  const handleIframeError = useCallback(() => {
    logger.warn("Iframe viewer failed to load", { url: fileUrl });
    setError(t("no_preview_available"));
    setIsLoading(false);
    onFileDownloadComplete?.("error");
    if (onStrategyFailure) onStrategyFailure();
  }, [t, fileUrl, onStrategyFailure, onFileDownloadComplete]);

  if (!fileUrl || !isValidUrl(fileUrl)) {
    return (
      <div className={styles.placeholderContent}>
        <Text size="large" color="subtle">
          {t("no_preview_available")}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.previewArea}>
      <div className={styles.previewControls}>
        <Text size="small" className={styles.zoomInfo}>
          {t("document_preview_label")}
        </Text>

        <Button
          variant="outline"
          size="medium"
          withIconEnd={<UIIcon src={ChevronDownLine} />}
          className={styles.zoomPercentage}
          disabled={true}
        >
          {zoomLevel}%
        </Button>

        <div className={styles.zoomButtons}>
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomInLine} />}
            onClick={onZoomIn}
            disabled={true}
            aria-label="Zoom in"
          />
          <Button
            variant="borderless"
            size="medium"
            withIconStart={<UIIcon src={ZoomOutLine} />}
            onClick={onZoomOut}
            disabled={true}
            aria-label="Zoom out"
          />
        </div>
      </div>

      <div className={styles.documentPreview}>
        <div className={styles.documentPage}>
          {isLoading && (
            <div className={styles.placeholderContent}>
              <Spinner monochromatic size="small" />
            </div>
          )}
          {error ? (
            <div className={styles.placeholderContent}>
              <Text size="large" color="subtle">
                {error}
              </Text>
            </div>
          ) : (
            <>
              <iframe
                src={fileUrl}
                title={result.title || "Document Preview"}
                style={{
                  width: "100%",
                  height: "600px",
                  border: "none",
                  borderRadius: "var(--dig-border-radius__small)",
                }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-same-origin allow-scripts allow-forms"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const UnsupportedViewer: React.FC<ViewerProps> = ({ result: _result }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.previewArea}>
      <div className={styles.placeholderContent}>
        <Text size="large" color="subtle">
          {t("no_preview_available")}
        </Text>
        <Text size="small" color="subtle">
          File type not supported for preview
        </Text>
      </div>
    </div>
  );
};

const getFileType = (url: string, mimeType?: string): FileType => {
  if (mimeType) {
    if (mimeType.includes("pdf") || mimeType === "application/pdf") {
      logger.debug("Detected PDF via MIME type", { mimeType });
      return "pdf";
    }
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.startsWith("text/")) return "text";
  }

  const extension = url.split(".").pop()?.toLowerCase() || "";

  switch (extension) {
    case "pdf":
      logger.debug("Detected PDF via extension");
      return "pdf";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
    case "svg":
      return "image";
    case "mp4":
    case "webm":
    case "ogg":
    case "avi":
    case "mov":
      return "video";
    case "mp3":
    case "wav":
    case "flac":
      return "audio";
    case "txt":
    case "md":
    case "json":
    case "xml":
    case "html":
    case "css":
    case "js":
    case "ts":
      return "text";
    default:
      logger.debug("File type unknown", { url, mimeType, extension });
      return "unknown";
  }
};

const viewerStrategies: ViewerStrategy[] = [
  {
    type: "direct_pdfjs",
    canHandle: (url, mimeType) => getFileType(url, mimeType) === "pdf",
    priority: 100,
    component: DirectPdfViewer,
  },
  {
    type: "iframe",
    canHandle: (url, mimeType) => getFileType(url, mimeType) === "pdf",
    priority: 50,
    component: IframeViewer,
  },
  {
    type: "unsupported",
    canHandle: () => true,
    priority: 1,
    component: UnsupportedViewer,
  },
];

export const EnhancedFileViewer: React.FC<ViewerProps> = (props) => {
  const { result } = props;
  const { t } = useTranslation();
  const { analyticsLogsOpen } = useComponentConfig();

  logger.debug("EnhancedFileViewer rendered", {
    url: result.url,
    previewUrl: result.previewUrl,
    title: result.title,
    mimeType: result.mimeType,
  });

  const [currentStrategy, setCurrentStrategy] = useState<ViewerStrategy | null>(
    null,
  );
  const [failedStrategies, setFailedStrategies] = useState<Set<ViewerType>>(
    new Set(),
  );
  const [isRetryingStrategy, setIsRetryingStrategy] = useState(false);

  const fileUrl = result.url || result.previewUrl;
  const mimeType = result.mimeType;

  const availableStrategies = useMemo(() => {
    logger.debug("Evaluating strategies", { fileUrl, mimeType });

    const strategies = viewerStrategies
      .filter((strategy) => {
        const canHandle = strategy.canHandle(fileUrl || "", mimeType || "");
        const isNotFailed = !failedStrategies.has(strategy.type);

        logger.debug("Strategy evaluation", {
          strategy: strategy.type,
          canHandle,
          isNotFailed,
        });

        return canHandle && isNotFailed;
      })
      .sort((a, b) => b.priority - a.priority);

    logger.debug("Available strategies", {
      strategies: strategies.map((s) => `${s.type} (priority: ${s.priority})`),
      failedStrategies: Array.from(failedStrategies),
    });

    return strategies;
  }, [fileUrl, mimeType, failedStrategies]);

  useEffect(() => {
    if (availableStrategies.length > 0 && !currentStrategy) {
      const selectedStrategy = availableStrategies[0];
      logger.debug("Setting initial strategy", {
        selectedStrategy: selectedStrategy.type,
        availableStrategies: availableStrategies.map((s) => s.type),
        fileUrl,
        mimeType,
      });
      setCurrentStrategy(selectedStrategy);
    }
  }, [availableStrategies, currentStrategy, fileUrl, mimeType]);

  useEffect(() => {
    if (currentStrategy?.type === "direct_pdfjs" && !isRetryingStrategy) {
      logger.debug("Direct PDF.js strategy selected, monitoring for timeout");

      const timeout = setTimeout(() => {
        logger.debug(
          "PDF.js strategy timeout - checking if fallback is needed",
        );

        if (availableStrategies.length > 1) {
          logger.debug(
            "PDF.js may have failed, but letting it handle its own fallback logic",
          );
        }
      }, 20000);

      return () => clearTimeout(timeout);
    }
  }, [currentStrategy, isRetryingStrategy, availableStrategies]);

  const handleStrategyFailure = useCallback(() => {
    if (currentStrategy) {
      logger.warn("Viewer strategy failed, trying fallback", {
        failedStrategy: currentStrategy.type,
        fileUrl,
        mimeType,
      });

      setFailedStrategies((prev) => new Set([...prev, currentStrategy.type]));
      setCurrentStrategy(null);
      setIsRetryingStrategy(true);

      setTimeout(() => setIsRetryingStrategy(false), 1000);
    }
  }, [currentStrategy, fileUrl, mimeType]);

  useEffect(() => {
    logger.debug("Strategy state changed", {
      currentStrategy: currentStrategy?.type,
      availableStrategies: availableStrategies.map((s) => s.type),
      failedStrategies: Array.from(failedStrategies),
      fileUrl,
      mimeType,
    });
  }, [
    currentStrategy,
    availableStrategies,
    failedStrategies,
    fileUrl,
    mimeType,
  ]);

  if (!currentStrategy) {
    logger.debug("No strategy available, showing placeholder");
    return (
      <div className={styles.previewArea}>
        <div className={styles.placeholderContent}>
          <Text size="large" color="subtle">
            {t("no_preview_available")}
          </Text>
          {failedStrategies.size > 0 && (
            <Text size="small" color="subtle">
              This file may have CORS restrictions or be unsupported
            </Text>
          )}

          {analyticsLogsOpen && (
            <details style={{ marginTop: "10px", fontSize: "12px" }}>
              <summary>Debug Info</summary>
              <pre>
                {JSON.stringify(
                  {
                    fileUrl,
                    mimeType,
                    failedStrategies: Array.from(failedStrategies),
                    availableStrategies: availableStrategies.map((s) => s.type),
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  const ViewerComponent = currentStrategy.component;

  logger.debug("Rendering with strategy", { strategy: currentStrategy.type });

  return (
    <div data-viewer-type={currentStrategy.type}>
      <ViewerComponent {...props} onStrategyFailure={handleStrategyFailure} />
    </div>
  );
};
